/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {Mutation, useMutation, useQuery} from 'react-query';
import {WorkBook} from 'xlsx/types';
import {error} from '@ion/core/src/logger/logger';
import {getS3UploadUrl, uploadExcelToS3Bucket} from '@ion/core/src/services/Connect';
import {isSuccess} from '@ion/core/src/services/ServiceErrors';
import {GenericObject} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult, createQueryResult, getStaleTime} from '../utils/helpers';

export interface UseS3BucketUrlParams {
  userId: number;
  accountId: number;
  actionType: string;
  entityType: string;
  workbook?: WorkBook | Blob;
};

export type UseS3BucketUrlResult = {
  s3DownloadKey: string;
  isUploadingFile: boolean;
  uploadFile: (params: UseS3BucketUrlParams, callbacks) => void;
}

/**
 * The useS3BucketUrl hook is used to retrieve s3UploadUrl and s3DownloadKey
 * and upload the excel using s3UploadUrl.
 *
 * ```typescript
 *
 *  import {useS3BucketUrl} from '@ion/packages/query/src/hooks';
 *
 *  const {s3DownloadKey} = useS3BucketUrl({
 *    accountId,
 *    entityType,
 *    actionType,
 *    userId
 *  });
 *
 * ```
 */
export const useS3BucketUrl = (): UseS3BucketUrlResult => {
  const mutation = useMutation(async(params: UseS3BucketUrlParams) => {
    const {userId, accountId, entityType, actionType, workbook} = params;
    const payload = {
      userId,
      accountId,
      entityType,
      actionType
    };
    const result = await getS3UploadUrl(payload);
    const status = await uploadExcelToS3Bucket(result?.s3UploadUrl, workbook);
    if (isSuccess(status)) {
      return result?.s3DownloadKey;
    }
    return '';
  });

  return createMutationResult(
    mutation, {
      uploadFile      : mutation.mutate,
      isUploadingFile : mutation.isLoading
    }
  ) as UseS3BucketUrlResult;
};

export default useS3BucketUrl;
